import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import {
  Star, Target, CheckCircle, WarningCircle, ShieldCheck, PaperPlaneRight,
} from "@phosphor-icons/react";

/**
 * Public 360-degree feedback page. NO authentication required — anyone with
 * the token in the URL can rate the coachee's 20 competencies 1-5 stars.
 *
 * Critical promise to the reviewer (and shown in the UI): their name is
 * stored but NEVER returned to the coachee — the coachee only ever sees the
 * average score per competency.
 */
export default function CompetenceFeedbackPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [scores, setScores] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data: payload } = await api.get(`/competence-feedback/${token}`);
        setData(payload);
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleScore = (compName, value) => {
    setScores((prev) => ({ ...prev, [compName]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || Object.keys(scores).length === 0) return;
    setSubmitting(true);
    setError("");
    try {
      await api.post(`/competence-feedback/${token}`, {
        name: name.trim(),
        scores,
      });
      setSubmitted(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PublicShell>
        <div className="text-zinc-500">Laden...</div>
      </PublicShell>
    );
  }

  if (error && !data) {
    return (
      <PublicShell>
        <div className="neuro-card-bold p-6 bg-white" data-testid="feedback-error-screen">
          <div className="flex items-start gap-3">
            <WarningCircle size={24} weight="duotone" className="text-[#E53935] shrink-0 mt-0.5" />
            <div>
              <h2 className="font-display text-xl font-bold tracking-tight">Link niet beschikbaar</h2>
              <p className="text-sm text-zinc-600 mt-2">{error}</p>
            </div>
          </div>
        </div>
      </PublicShell>
    );
  }

  if (submitted) {
    return (
      <PublicShell>
        <div className="neuro-card-bold p-8 bg-white text-center" data-testid="feedback-thanks">
          <div className="inline-flex h-14 w-14 items-center justify-center bg-[#43A047]/10 text-[#43A047] rounded-sm mx-auto mb-4">
            <CheckCircle size={28} weight="duotone" />
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight">Bedankt voor je feedback!</h2>
          <p className="text-sm text-zinc-600 mt-2 max-w-md mx-auto leading-relaxed">
            Je scores zijn opgeslagen. <strong>{data?.coachee_first_name}</strong> ziet alleen het
            gemiddelde van alle feedback — jouw naam blijft geheim.
          </p>
        </div>
      </PublicShell>
    );
  }

  const competencies = data?.competencies || [];
  const allScored = competencies.length > 0 && competencies.every((c) => scores[c.name]);
  const someScored = Object.keys(scores).length > 0;

  return (
    <PublicShell>
      <div className="space-y-6" data-testid="feedback-page">
        <div className="neuro-card-bold p-6 bg-white">
          <div className="flex items-start gap-3">
            <Target size={26} weight="duotone" className="text-[#1E88E5] shrink-0 mt-0.5" />
            <div className="flex-1">
              <h1 className="font-display text-2xl font-bold tracking-tight">
                360°-feedback voor <span className="text-[#1E88E5]">{data?.coachee_first_name}</span>
              </h1>
              <p className="text-sm text-zinc-700 mt-2 leading-relaxed">
                Geef per competentie aan hoe goed jij vindt dat {data?.coachee_first_name} hierin is.
                Klik 1 t/m 5 sterren. Sla competenties die je niet kunt beoordelen gerust over —
                geef minstens één score.
              </p>
            </div>
          </div>
          <div className="mt-4 border-l-4 border-[#43A047] bg-[#43A047]/5 px-4 py-3 rounded-r-sm flex items-start gap-2.5" data-testid="feedback-privacy-notice">
            <ShieldCheck size={20} weight="duotone" className="text-[#43A047] shrink-0 mt-0.5" />
            <p className="text-sm text-zinc-700 leading-relaxed">
              <strong>Je naam blijft geheim.</strong> {data?.coachee_first_name} ziet alleen het
              gemiddelde van alle feedback per competentie, nooit individuele scores of namen.
              Vul wel je naam in zodat de work coach kan zien hoeveel mensen feedback gaven.
            </p>
          </div>
        </div>

        {error && data && (
          <div className="border border-[#E53935]/30 bg-[#E53935]/5 text-[#E53935] text-sm px-4 py-3 rounded-sm" data-testid="feedback-form-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="feedback-form">
          <div className="neuro-card p-5 bg-white">
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1.5">
              Jouw naam (alleen zichtbaar voor de work coach)
            </label>
            <input
              type="text"
              required
              maxLength={100}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Voor- en achternaam"
              data-testid="feedback-name-input"
              className="w-full px-3 py-2.5 border-2 border-zinc-200 rounded-sm text-sm focus:outline-none focus:border-zinc-900"
            />
          </div>

          <div className="space-y-2" data-testid="feedback-competency-list">
            {competencies.map((c, i) => (
              <div
                key={c.name}
                className="neuro-card p-4 bg-white grid grid-cols-1 md:grid-cols-12 gap-3 items-center"
                data-testid={`feedback-row-${i}`}
              >
                <div className="md:col-span-7 min-w-0">
                  <h4 className="font-display text-base font-bold tracking-tight">{c.name}</h4>
                  {c.description && (
                    <p className="text-xs text-zinc-600 mt-1 leading-relaxed">{c.description}</p>
                  )}
                </div>
                <div className="md:col-span-5 flex md:justify-end items-center gap-1" data-testid={`feedback-stars-${i}`}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => handleScore(c.name, n)}
                      aria-label={`${c.name} score ${n}`}
                      data-testid={`feedback-star-${i}-${n}`}
                      className="p-1 transition-transform hover:scale-110 outline-none focus:outline-none"
                    >
                      <Star
                        size={22}
                        weight={scores[c.name] && n <= scores[c.name] ? "fill" : "regular"}
                        className={scores[c.name] && n <= scores[c.name] ? "text-[#FB8C00]" : "text-zinc-300"}
                      />
                    </button>
                  ))}
                  {scores[c.name] && (
                    <button
                      type="button"
                      onClick={() => {
                        const copy = { ...scores };
                        delete copy[c.name];
                        setScores(copy);
                      }}
                      className="text-[10px] uppercase tracking-wide text-zinc-400 hover:text-zinc-700 ml-2"
                      data-testid={`feedback-clear-${i}`}
                    >
                      wissen
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-zinc-500">
              {Object.keys(scores).length} van {competencies.length} competenties gescoord.
              {allScored && " Compleet!"}
            </p>
            <button
              type="submit"
              disabled={!name.trim() || !someScored || submitting}
              data-testid="feedback-submit-btn"
              className="inline-flex items-center gap-2 bg-zinc-900 text-white px-6 py-2.5 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] disabled:bg-zinc-300 disabled:cursor-not-allowed transition-colors"
            >
              <PaperPlaneRight size={16} /> {submitting ? "Versturen..." : "Verstuur feedback"}
            </button>
          </div>
        </form>
      </div>
    </PublicShell>
  );
}

function PublicShell({ children }) {
  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="border-b-2 border-zinc-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-2.5">
          <div className="inline-flex h-9 w-9 items-center justify-center bg-[#1E88E5] text-white rounded-sm">
            <Target size={18} weight="fill" />
          </div>
          <div>
            <div className="font-display text-base font-bold tracking-tight">Werk Dat Past Online</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">360° feedback</div>
          </div>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-6 py-8">{children}</main>
    </div>
  );
}
